import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../../engine/api/context';
import { useInvalidateResource, useResourceList } from '../../engine/api/hooks';
import { ApiError } from '../../engine/api/client';
import { Button } from '../../engine/design/primitives/Button';
import { Field, Input, Select } from '../../engine/design/primitives/Input';
import { Banner } from '../../engine/design/primitives/Misc';
import { Dialog } from '../../engine/design/primitives/Overlay';
import { useToast } from '../../engine/design/primitives/Toast';
import { RelationPicker } from '../../engine/fields/RelationPicker';
import type { FieldMeta } from '../../engine/meta/types';
import type { ScreenContext } from '../../engine/screens/types';

// "Invite to portal": a contact picker + portal role + optional email override that posts
// POST /v1/portal-users/invite. Used as (a) the collection action on the Portal users list
// (opens automatically for /portal-users?invite=<contactId|1>, the palette command's target) and
// (b) the `portal-invite` field widget that a contact record's `portal_access_level` field can
// declare, so the same dialog is reachable from the contact itself.

const CONTACT_FIELD: FieldMeta = { name: 'contact_id', label: 'Contact', type: 'relation', relation: { resource: 'contacts', displayField: 'name' }, required: true, readOnly: false, nullable: false, filterable: false, sortable: false, searchable: false, inCreate: true, inUpdate: false, inList: false, inDetail: false };

interface RoleRow { id: string; key: string; name: string; is_default: boolean }

export function InviteDialog({ open, onClose, contactId, onDone }: { open: boolean; onClose: () => void; contactId?: string | null; onDone?: () => void }) {
  const api = useApi();
  const toast = useToast();
  const invalidate = useInvalidateResource();
  const roles = useResourceList<RoleRow>('portal-roles', { sort: 'sort' }, { limit: 100, enabled: open });
  const [contact, setContact] = useState<string | null>(contactId ?? null);
  const [roleId, setRoleId] = useState<string>('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setContact(contactId ?? null);
      setError(null);
      setEmail('');
    }
  }, [open, contactId]);
  const submit = async () => {
    if (!contact) return;
    setBusy(true);
    setError(null);
    try {
      await api.resource('portal-users').action('invite', { body: { contact_id: contact, ...(roleId ? { role_id: roleId } : {}), ...(email.trim() ? { email: email.trim() } : {}) } });
      toast({ title: 'Invitation sent' });
      invalidate('portal-users');
      invalidate('contacts', contact);
      onDone?.();
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} title="Invite to the customer portal" description="The contact receives an email with a link to set up portal sign-in. Their portal role decides what they can do; their contact's access level decides whose tickets they see." locked={busy}>
      <div className="hf-stack">
        {!contactId && (
          <Field label="Contact" required>
            <RelationPicker field={CONTACT_FIELD} value={contact} onChange={(v) => setContact((v as string | null) ?? null)} autoFocus />
          </Field>
        )}
        <Field label="Portal role" help="Leave as default to use the tenant's default portal role.">
          <Select value={roleId} onChange={(e) => setRoleId(e.target.value)} placeholder="Default role" options={roles.rows.map((r) => ({ value: r.id, label: r.is_default ? `${r.name} (default)` : r.name }))} />
        </Field>
        <Field label="Send to a different email" help="Optional; defaults to the contact's email.">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        {error && <Banner tone="error">{error}</Banner>}
        <div className="hf-form-actions">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" busy={busy} disabled={!contact} onClick={() => void submit()}>Send invitation</Button>
        </div>
      </div>
    </Dialog>
  );
}

/** Collection action on the Portal users list. `?invite=1` (palette) or `?invite=<contactId>` opens it. */
export function InviteAction({ refresh }: ScreenContext & { selected?: string[] }) {
  const [sp, setSp] = useSearchParams();
  const param = sp.get('invite');
  const [open, setOpen] = useState(Boolean(param));
  useEffect(() => setOpen(Boolean(param)), [param]);
  const contactId = useMemo(() => (param && param !== '1' ? param : null), [param]);
  const close = () => {
    setOpen(false);
    if (param) {
      const next = new URLSearchParams(sp);
      next.delete('invite');
      setSp(next, { replace: true });
    }
  };
  return (
    <>
      <Button variant="primary" icon="user-plus" onClick={() => setOpen(true)}>Invite contact</Button>
      <InviteDialog open={open} onClose={close} contactId={contactId} onDone={refresh} />
    </>
  );
}

/** Field widget for a contact's `portal_access_level`: the value plus an "Invite to portal" button. */
export function PortalInviteWidget({ value, row }: { value: unknown; row?: Record<string, unknown> }) {
  const [open, setOpen] = useState(false);
  const id = row?.['id'] as string | undefined;
  const label = { none: 'No portal access', own: 'Own tickets', site: 'Site tickets', client: 'All client tickets' }[String(value ?? 'none')] ?? String(value ?? '—');
  return (
    <span className="hf-row">
      <span>{label}</span>
      {id && <Button size="sm" variant="outline" icon="mail" onClick={() => setOpen(true)}>Invite to portal</Button>}
      {id && <InviteDialog open={open} onClose={() => setOpen(false)} contactId={id} />}
    </span>
  );
}
